import { useEffect, useCallback, useMemo } from "react";
import { useOrderStore } from "../store/orderStore.jsx";
import { validateOTOPrice } from "../utils/otoValidation.js";
import marketdata from "../data/Services/marketdata";
import { submitOrderToRouter } from "../utils/orderSubmission.js";
import { determineOrderType } from "../utils/orderTypeLogic.js";
import { router } from "../data/Services/ordersRouter.jsx";
import { globalToast } from "../components/ToastProvider.jsx";
import {
  normalizePrice,
  offsetByTicks,
  computeTicksBetween,
} from "../utils/priceUtils.js";
import { buildReplaceOrder } from "../utils/buildReplaceOrder.js";
import { buildOtoLegs } from "../utils/otoLegs.js";
import { useOrderDefaultsStore } from "./useOrderDefaults.js";
// NOTE: deltaTicks derivation for OTO orders is centralized here (updateOTOPrice) to ensure
// we always use the freshest main order price AFTER any main drag completion. This avoids
// stale mainOrder.price issues observed when computing delta inside drag components.

// Hook to manage order overlay data and creation
// Works with symbols, not chartIds - multiple charts with same symbol share orders
// Supports: Local orders, Router orders (including OCO), OTO orders
// Returns: { orders, createOrder, updateOrderPrice, submitOrder, removeOrder }
export const useOrderOverlay = (symbol) => {
  // Get the full ordersBySymbol object - single source of truth
  const ordersBySymbol = useOrderStore((state) => state.ordersBySymbol);

  // Use useMemo to get stable orders array for the symbol, including orders from master symbol
  const orders = useMemo(() => {
    if (!symbol) return [];

    let allOrders = [...(ordersBySymbol[symbol] || [])];

    // Simple approach: include orders from all symbols that could be related
    // This ensures orders are visible across symbol variations
    const allSymbolsWithOrders = Object.keys(ordersBySymbol);

    // #356: funde APENAS buckets que são o MESMO instrumento tradeável — o alias
    // (WINFUT) e o contrato corrente (WINQ26) resolvem pro mesmo via resolveSymbol.
    // Antes, qualquer símbolo ≤6 chars sugava todo bucket com o mesmo prefixo de 3
    // chars → pintava ordens de vencimentos-IRMÃOS (WINX25) e de equities-irmãs
    // (PETR3 no chart de PETR4), e o trash cancelava o contrato/ativo errado.
    const resolvedSelf = marketdata.resolveSymbol(symbol);
    allSymbolsWithOrders.forEach((otherSymbol) => {
      if (
        otherSymbol !== symbol &&
        marketdata.resolveSymbol(otherSymbol) === resolvedSelf
      ) {
        allOrders.push(...ordersBySymbol[otherSymbol]);
      }
    });

    return allOrders;
  }, [ordersBySymbol, symbol]);

  // Get order store actions
  const addOrder = useOrderStore((state) => state.addOrder);
  const updateOrder = useOrderStore((state) => state.updateOrder);
  const removeOrderFromStore = useOrderStore((state) => state.removeOrder);
  const setPendingModification = useOrderStore(
    (state) => state.setPendingModification,
  );

  // Get OTO store actions
  const updateOTOInOrder = useOrderStore((state) => state.updateOTOInOrder);
  const removeOTOFromOrder = useOrderStore((state) => state.removeOTOFromOrder);

  // Create a new local order
  const createOrder = useCallback(
    (orderSymbol, side, price, quantity, currentPrice = null, options = {}) => {
      if (!orderSymbol) {
        console.warn(
          "useOrderOverlay: No symbol provided, cannot create order",
        );
        return null;
      }

      // Gated order-resolution authority: blocks a FUT/FUL draft when the symbol
      // table isn't this trading day's set, so the draft can't resolve to the wrong
      // contract (#318/#351). Explicit contracts pass through ungated. Self-handles
      // the block (toast + return null) so the 6 createOrder call sites need no try/catch.
      let translatedSymbol;
      try {
        translatedSymbol = marketdata.resolveSymbolForTrading(orderSymbol);
      } catch (e) {
        // options.silent: caller (e.g. a post-failed-submit fallback) already
        // surfaced the error — suppress the duplicate toast (#351 review).
        if (!options.silent)
          globalToast.showError(e.message, "Não foi possível criar a ordem");
        return null;
      }

      // Normalize price to valid tick increment
      const normalizedPrice = normalizePrice(orderSymbol, price);
      console.log("useOrderOverlay: Price normalization:", {
        original: price,
        normalized: normalizedPrice,
      });

      // Determine order type based on price comparison with current price
      const orderType = determineOrderType(side, normalizedPrice, currentPrice);

      const newOrder = {
        id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        symbol: translatedSymbol, // Use translated symbol for consistency with router
        state: "local",
        isLocal: true, // Flag to preserve local orders when router disconnects
        side,
        price: normalizedPrice, // Use normalized price
        quantity,
        orderType,
      };

      console.log("useOrderOverlay: Creating new order:", newOrder);
      addOrder(translatedSymbol, newOrder); // Store under translated symbol
      return newOrder;
    },
    [addOrder],
  );

  // Shared main drag application (unified for local & open optimistic updates)
  // Responsibilities:
  // - Derive updated orderType based on current market price
  // - Ensure each existing OTO leg has deltaTicks (relative spacing)
  // - Recalculate OTO prices based on NEW main price + deltaTicks (fixes snap-back bug)
  // - Persist price/orderType + OTO with updated prices to store for optimistic UI
  const applyMainDrag = useCallback(
    (
      targetOrder,
      targetNormalizedPrice,
      marketPrice,
      targetOrderId,
      preserveOrderType = false,
    ) => {
      const newOrderType = preserveOrderType
        ? targetOrder.orderType
        : determineOrderType(
            targetOrder.side,
            targetNormalizedPrice,
            marketPrice,
          );
      const updates = { price: targetNormalizedPrice, orderType: newOrderType };
      if (targetOrder.oto && targetOrder.oto.length > 0) {
        updates.oto = targetOrder.oto.map((leg) => {
          let deltaTicks = leg.deltaTicks;

          // Calculate deltaTicks if not present (relative distance from main order)
          if (deltaTicks === undefined) {
            try {
              deltaTicks = computeTicksBetween(
                targetOrder.symbol,
                targetOrder.price,
                leg.price,
              );
            } catch {
              return leg; // Can't calculate delta, return as-is
            }
          }

          // Recalculate OTO price based on NEW main price + deltaTicks
          try {
            const newOTOPrice = offsetByTicks(
              targetOrder.symbol,
              targetNormalizedPrice,
              deltaTicks,
            );
            return { ...leg, deltaTicks, price: newOTOPrice };
          } catch {
            return { ...leg, deltaTicks };
          }
        });
      }
      updateOrder(targetOrder.symbol, targetOrderId, updates);
    },
    [updateOrder],
  );

  // Update order price (for drag editing)
  const updateOrderPrice = useCallback(
    async (orderId, newPrice, currentPrice = null) => {
      if (!symbol) return;

      // Translate symbol to match the symbol used during order creation
      const translatedSymbol = marketdata.resolveSymbol(symbol);

      // Normalize the new price using centralized utility
      const normalizedPrice = normalizePrice(translatedSymbol, newPrice);

      // Find the order to check its state
      const order = orders.find((o) => o.id === orderId);

      if (!order) {
        console.warn("useOrderOverlay.updateOrderPrice: Order not found:", {
          orderId,
          availableOrders: orders.map((o) => ({ id: o.id, state: o.state })),
        });
        return;
      }

      const marketPrice =
        currentPrice || marketdata?.getLastQuote?.(symbol)?.C || null;

      if (order.state === "local") {
        applyMainDrag(order, normalizedPrice, marketPrice, orderId);
      } else if (order.state === "open") {
        // Open orders: modify via router — keep original orderType (price-only change)
        try {
          // Optimistic update: update price locally but preserve orderType
          applyMainDrag(order, normalizedPrice, marketPrice, orderId, true);
          console.log(
            "✅ Optimistically updated order price to:",
            normalizedPrice,
          );

          // #353: applyMainDrag recalculou as pernas OTO no STORE (paint movido),
          // mas `order` é o snapshot PRÉ-drag (updateOrder faz {...order}, nunca muta
          // a ref). Sem re-ler a ordem fresca, buildReplaceOrder mandaria os preços
          // ANTIGOS das pernas → corretora recebe stop/target stale enquanto a tela
          // mostra movido. Re-ler garante que o wire case com o paint otimista.
          const freshOrder =
            useOrderStore
              .getState()
              .ordersBySymbol[order.symbol]?.find((o) => o.id === orderId) ||
            order;

          // Build replace order via unified utility
          const replaceOrderObject = buildReplaceOrder(
            freshOrder,
            {
              price: normalizedPrice,
            },
            translatedSymbol,
          );

          console.log(
            "🚀 Calling router.replaceOrder with:",
            JSON.stringify(replaceOrderObject, null, 2),
          );

          // Set pending state for visual feedback
          setPendingModification(orderId, true);

          try {
            await router.replaceOrder(replaceOrderObject);
            console.log("✅ Order replacement sent to router");
          } finally {
            // Clear pending state (success or error)
            setPendingModification(orderId, false);
          }
        } catch (error) {
          console.error("❌ Failed to modify open order price:", error);

          // Revert to ROUTER-CONFIRMED prices (originalRouterOrder has the server's last known state)
          // This is the most reliable source for revert - the prices the server actually has
          const originalPrice =
            order.originalRouterOrder?.stop ??
            order.originalRouterOrder?.limit ??
            order.price;
          const originalOTO =
            order.originalRouterOrder?.oto?.map((leg) => ({
              ...leg,
              price: leg.limit ?? leg.stop ?? leg.price,
            })) ?? order.oto;

          // Use order.symbol (actual storage key) not hook's symbol
          updateOrder(order.symbol, orderId, {
            price: originalPrice,
            oto: originalOTO,
          });
          console.log("🔄 Reverted order to router-confirmed state:", {
            price: originalPrice,
            oto: originalOTO?.map((leg) => ({
              id: leg.id,
              price: leg.price,
              limit: leg.limit,
              stop: leg.stop,
            })),
          });

          // Force sync with server to ensure correct state after failed replace
          // This handles cases where the rejection report arrives before this catch block
          try {
            await router.getAccountDataAsync();
            console.log(
              "📊 Refreshed accountData after failed replace operation",
            );
          } catch (refreshError) {
            console.warn(
              "⚠️ Failed to refresh accountData after replace error:",
              refreshError,
            );
          }

          alert(`Erro ao modificar preço da ordem: ${error.message || error}`);
        }
      } else {
        console.warn(
          "useOrderOverlay.updateOrderPrice: Cannot modify order in state:",
          order.state,
        );
      }
    },
    [symbol, orders, updateOrder, applyMainDrag, setPendingModification],
  );

  // Update order quantity
  const updateOrderQuantity = useCallback(
    async (orderId, newQuantity) => {
      if (!symbol) return;

      // Find the order to check its state
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        console.warn(
          "useOrderOverlay.updateOrderQuantity: Order not found:",
          orderId,
        );
        return;
      }

      console.log("🔢 useOrderOverlay.updateOrderQuantity:", {
        orderId,
        newQuantity,
        orderState: order.state,
      });

      if (order.state === "local") {
        // Local orders: update in store using the order's stored symbol (not the original symbol)
        const orderSymbol = order.symbol; // Use the symbol the order was stored under
        console.log(
          "🔍 useOrderOverlay.updateOrderQuantity: Using order symbol:",
          { originalSymbol: symbol, orderSymbol },
        );
        updateOrder(orderSymbol, orderId, { quantity: newQuantity });
      } else if (order.state === "open") {
        // Open orders: modify via router using translated symbol
        try {
          // Translate symbol for router operations (WINFUT → WINV25)
          const translatedSymbol = marketdata.resolveSymbol(symbol);
          console.log(
            "🔍 useOrderOverlay.updateOrderQuantity: Symbol translation:",
            { original: symbol, translated: translatedSymbol },
          );

          // Optimistic local update
          const orderSymbol = order.symbol || symbol;
          updateOrder(orderSymbol, orderId, { quantity: newQuantity });

          // Build replace order via unified utility
          const replaceOrderObject = buildReplaceOrder(
            order,
            {
              quantity: newQuantity,
            },
            translatedSymbol,
          );

          console.log(
            "🔢 useOrderOverlay.updateOrderQuantity: Sending replace order:",
            replaceOrderObject,
          );
          await router.replaceOrder(replaceOrderObject);
          console.log("✅ Open order quantity modified successfully");
        } catch (error) {
          console.error("❌ Failed to modify open order quantity:", error);

          // Force sync with server to ensure correct state after failed replace
          try {
            await router.getAccountDataAsync();
            console.log(
              "📊 Refreshed accountData after failed quantity replace operation",
            );
          } catch (refreshError) {
            console.warn(
              "⚠️ Failed to refresh accountData after replace error:",
              refreshError,
            );
          }

          alert(
            `Erro ao modificar quantidade da ordem: ${error.message || error}`,
          );
        }
      } else {
        console.warn(
          "useOrderOverlay.updateOrderQuantity: Cannot modify order in state:",
          order.state,
        );
      }
    },
    [symbol, orders, updateOrder],
  );

  // Submit local order to router
  const submitOrder = useCallback(
    async (orderId, onLoginRequired) => {
      if (!symbol) {
        console.warn("useOrderOverlay.submitOrder: No symbol provided");
        return false;
      }

      const order = orders.find((o) => o.id === orderId);
      console.log("📝 useOrderOverlay.submitOrder: Found order:", order);

      if (order && order.state === "local") {
        console.log(
          "✅ useOrderOverlay.submitOrder: Submitting order to router",
        );

        // OTO legs are persisted centrally inside submitOrderToRouter (pendingOTO),
        // so the local→router re-attach works for every submit path.

        // Submit to router using shared utility
        const submitted = await submitOrderToRouter(
          order,
          onLoginRequired, // Callback to open login modal
          () => {
            // Success callback - remove order from chart orders since it's now handled by router
            console.log(
              "✅ Order submitted to router, removing from chart orders",
            );
            console.log("🔍 Removing order:", {
              orderId,
              orderSymbol: order.symbol,
              hookSymbol: symbol,
            });
            removeOrderFromStore(order.symbol, orderId); // Use order's symbol (translated), not hook's symbol
          },
          (error) => {
            // Error callback
            console.error("❌ Failed to submit order:", error);
            globalToast.showError(
              error.message,
              "Não foi possível enviar agora",
            );
          },
        );

        return submitted;
      } else {
        console.log(
          "❌ useOrderOverlay.submitOrder: Order not found or not local:",
          { order, state: order?.state },
        );
        return false;
      }
    },
    [symbol, orders, removeOrderFromStore],
  );

  // Remove order from store (for local orders) or cancel via router (for open orders)
  const removeOrder = useCallback(
    async (orderId) => {
      if (!symbol) return;

      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        console.warn("useOrderOverlay.removeOrder: Order not found:", orderId);
        return;
      }

      console.log("useOrderOverlay: Removing/cancelling order:", {
        orderId,
        state: order.state,
        orderSymbol: order.symbol,
        hookSymbol: symbol,
      });

      if (order.state === "local") {
        // Remove local orders from store using the order's actual symbol (translated)
        removeOrderFromStore(order.symbol, orderId);
      } else if (order.state === "open" || order.state === "pending") {
        // Cancel open/pending orders via router
        try {
          if (router && router.cancelOrder) {
            await router.cancelOrder(order);
            console.log("✅ Order cancelled via router:", orderId);
          } else {
            console.warn("❌ Router not available for order cancellation");
          }
        } catch (error) {
          console.error("❌ Failed to cancel order via router:", error);

          // If the order doesn't exist in router anymore (already cancelled/filled),
          // remove it from local store to keep UI in sync
          if (
            error.message?.includes("não existir") ||
            error.message?.includes("já foi cancelada")
          ) {
            // #354: NÃO remover a linha aqui — o bool-false genérico do router NÃO
            // distingue transient-send-failure (ordem VIVA) de not-found (sumida);
            // remover esconderia uma ordem viva (cancel-órfã). Só força um accountData
            // fresco: a reconciliação (handleAccountDataUpdate) remove a ordem SÓ se
            // ela sumiu de openOrders e a mantém se ainda viva; e o report final-state
            // remove quando o broker confirma o cancel.
            if (router && router.getAccountDataAsync) {
              router._lastAccountDataString = null; // busta o cache de dedup
              router
                .getAccountDataAsync()
                .catch((e) =>
                  console.warn(
                    "Failed to refresh account data after cancel error:",
                    e,
                  ),
                );
            }
          } else {
            // For other errors, show alert
            alert(`Erro ao cancelar ordem: ${error.message}`);
          }
        }
      } else {
        console.warn("❌ Cannot remove order in state:", order.state);
      }
    },
    [symbol, orders, removeOrderFromStore],
  );

  // OTO-related methods

  // Create OTO stop+profit legs for a main order.
  // Accepts optional mainOrderArg to avoid stale-closure race when called immediately
  // after createOrder() in the same callback. Falls back to fresh store state.
  // Explicit stopPrice/profitPrice override per-symbol defaults (drag/edit paths).
  // Idempotent: replaces existing -S/-P legs instead of appending duplicates.
  const createOTO = useCallback(
    (
      mainOrderId,
      stopPrice = null,
      profitPrice = null,
      mainOrderArg = null,
    ) => {
      // Prefer caller-supplied order; fall back to fresh store state to avoid stale closure
      let mainOrder = mainOrderArg;
      if (!mainOrder) {
        const freshOrders = useOrderStore.getState().ordersBySymbol;
        const allSymbolOrders = Object.values(freshOrders).flat();
        mainOrder = allSymbolOrders.find((o) => o.id === mainOrderId);
      }
      if (!mainOrder) {
        console.warn(
          "useOrderOverlay.createOTO: Main order not found:",
          mainOrderId,
        );
        return;
      }

      const translatedSymbol =
        mainOrder.symbol || marketdata.resolveSymbol(symbol);

      let stopLeg, profitLeg;

      if (stopPrice !== null || profitPrice !== null) {
        // Explicit prices supplied (drag/edit paths) — build legs preserving
        // the existing per-tick-offset semantics, normalizing prices.
        const resolvedStop =
          stopPrice !== null
            ? normalizePrice(translatedSymbol, stopPrice)
            : null;
        const resolvedProfit =
          profitPrice !== null
            ? normalizePrice(translatedSymbol, profitPrice)
            : null;

        const oppositeSide = mainOrder.side === "buy" ? "sell" : "buy";
        stopLeg = {
          id: `${mainOrderId}-S`,
          type: "stop",
          orderType: "STOPLIMIT",
          price:
            resolvedStop ??
            offsetByTicks(
              translatedSymbol,
              mainOrder.price,
              mainOrder.side === "buy" ? -10 : 10,
            ),
          quantity: mainOrder.quantity,
          symbol: translatedSymbol,
          side: oppositeSide,
          state: "local",
        };
        profitLeg = {
          id: `${mainOrderId}-P`,
          type: "profit",
          orderType: "LIMIT",
          price:
            resolvedProfit ??
            offsetByTicks(
              translatedSymbol,
              mainOrder.price,
              mainOrder.side === "buy" ? 10 : -10,
            ),
          quantity: mainOrder.quantity,
          symbol: translatedSymbol,
          side: oppositeSide,
          state: "local",
        };
      } else {
        // Use per-symbol configured defaults from the store
        const otoConfig = useOrderDefaultsStore
          .getState()
          .getOtoConfig(translatedSymbol);
        const legs = buildOtoLegs({
          mainOrderId,
          symbol: translatedSymbol,
          side: mainOrder.side,
          quantity: mainOrder.quantity,
          anchorPrice: mainOrder.price,
          lossTicks: otoConfig.lossTicks,
          profitTicks: otoConfig.profitTicks,
        });
        if (!legs.length) {
          console.warn(
            "useOrderOverlay.createOTO: buildOtoLegs returned empty for:",
            { mainOrderId, translatedSymbol, mainOrder },
          );
          return;
        }
        [stopLeg, profitLeg] = legs;
      }

      // Idempotent: replace any existing -S/-P legs, do not append duplicates
      const existingOto = mainOrder.oto || [];
      const filteredOto = existingOto.filter(
        (leg) => leg.id !== stopLeg.id && leg.id !== profitLeg.id,
      );
      const newOto = [...filteredOto, stopLeg, profitLeg];
      updateOrder(mainOrder.symbol, mainOrderId, { oto: newOto });

      return { stopOTO: stopLeg, profitOTO: profitLeg };
    },
    [symbol, updateOrder],
  );

  // Update OTO order price - central deltaTicks derivation for consistency
  const updateOTOPrice = useCallback(
    async (mainOrderId, otoOrderId, newPrice, options = {}) => {
      // Centralized delta calculation path
      console.log("🎯 useOrderOverlay.updateOTOPrice called:", {
        mainOrderId,
        otoOrderId,
        newPrice,
        options,
      });

      // ✅ CRITICAL: Reject NaN/Infinity prices early to prevent store corruption
      if (!isFinite(newPrice)) {
        console.error(
          "❌ updateOTOPrice: Rejected invalid price (NaN or Infinity):",
          { mainOrderId, otoOrderId, newPrice },
        );
        return;
      }

      let translatedSymbol = marketdata.resolveSymbol(symbol);

      // Pull freshest main order from store (post any main drag updates)
      // Try translated symbol first, then fall back to searching all related symbols
      let currentOrders = useOrderStore
        .getState()
        .getOrdersForSymbol(translatedSymbol);
      let mainOrder = currentOrders.find((o) => o.id === mainOrderId);

      // If not found, try searching in related symbols (WIN family)
      if (!mainOrder) {
        const prefix = symbol.substring(0, 3);
        const relatedSymbols = [
          symbol,
          translatedSymbol,
          prefix + "FUT",
          prefix + "FUL",
        ];
        for (const sym of relatedSymbols) {
          const orders = useOrderStore.getState().getOrdersForSymbol(sym);
          mainOrder = orders.find((o) => o.id === mainOrderId);
          if (mainOrder) {
            translatedSymbol = sym; // Use the symbol where we found the order
            break;
          }
        }
      }

      if (!mainOrder) {
        console.warn("updateOTOPrice: Main order not found:", mainOrderId);
        return;
      }

      // ✅ VALIDATE AND CLAMP OTO PRICE TO REASONABLE RANGE
      // Skip validation for programmatic updates (e.g., during main order drag) because:
      // 1. Prices were already validated during drag using the DRAGGED main price
      // 2. The main order price in store is still OLD at this point, causing false validation failures
      let finalPrice = newPrice;

      if (!options.programmatic) {
        const otoType = otoOrderId.endsWith("-S") ? "stop" : "profit";
        const validation = validateOTOPrice(
          newPrice,
          mainOrder.price,
          mainOrder.side,
          otoType,
          translatedSymbol,
        );
        finalPrice = validation.price;

        if (validation.clamped) {
          console.warn("🛡️ OTO price clamped for safety:", {
            original: newPrice,
            clamped: validation.price,
            reason: validation.reason,
            mainPrice: mainOrder.price,
            side: mainOrder.side,
            otoType,
          });
          // Show user feedback about clamping
          // Note: You might want to show a toast notification here
        }
      }

      const normalizedPrice = normalizePrice(translatedSymbol, finalPrice);

      const otoOrder = mainOrder?.oto?.find((o) => o.id === otoOrderId);

      let deltaTicks = options.deltaTicks;
      if (deltaTicks === undefined && mainOrder && isFinite(mainOrder.price)) {
        deltaTicks = computeTicksBetween(
          translatedSymbol,
          mainOrder.price,
          normalizedPrice,
        );
      }

      const updates = { price: normalizedPrice };
      if (deltaTicks !== undefined) updates.deltaTicks = deltaTicks;

      // Capture original OTO price BEFORE optimistic update (for revert on error)
      const originalOTOPrice = otoOrder?.price;
      const originalOTODeltaTicks = otoOrder?.deltaTicks;

      updateOTOInOrder(translatedSymbol, mainOrderId, otoOrderId, updates);

      // ✅ IF MAIN ORDER IS OPEN, SEND REPLACE ORDER TO ROUTER
      // Skip router call if explicitly requested (e.g., during main order drag where OTO prices
      // are committed to store first, then main order update sends single consolidated router call)
      if (options.skipRouterCall) {
        console.log(
          "⏭️ Skipping router call for OTO update (will be sent with main order):",
          { mainOrderId, otoOrderId, newPrice: normalizedPrice },
        );
      }
      if (mainOrder.state === "open" && !options.skipRouterCall) {
        console.log(
          "🔄 Main order is open, sending OTO update to router via replaceOrder",
        );

        // Set pending state for visual feedback (on main order since OTO is part of it)
        setPendingModification(mainOrderId, true);

        try {
          // Build replace order via unified utility — only OTO price changed
          const replaceOrderObject = buildReplaceOrder(
            mainOrder,
            {
              otoUpdates: [{ otoId: otoOrderId, newPrice: normalizedPrice }],
            },
            translatedSymbol,
          );

          // Send to router
          await router.replaceOrder(replaceOrderObject);
          console.log("✅ Successfully sent OTO update to router");
        } catch (error) {
          console.error("❌ Failed to send OTO update to router:", error);

          // Revert OTO price to original state before optimistic update
          if (originalOTOPrice !== undefined) {
            const revertUpdates = { price: originalOTOPrice };
            if (originalOTODeltaTicks !== undefined)
              revertUpdates.deltaTicks = originalOTODeltaTicks;
            updateOTOInOrder(
              translatedSymbol,
              mainOrderId,
              otoOrderId,
              revertUpdates,
            );
            console.log("🔄 Reverted OTO price to original:", {
              otoOrderId,
              originalOTOPrice,
            });
          }

          // Force sync with server to ensure correct state after failed replace
          try {
            await router.getAccountDataAsync();
            console.log(
              "📊 Refreshed accountData after failed OTO update operation",
            );
          } catch (refreshError) {
            console.warn(
              "⚠️ Failed to refresh accountData after replace error:",
              refreshError,
            );
          }

          alert(
            `Erro ao modificar preço do stop/alvo: ${error.message || error}`,
          );
        } finally {
          // Clear pending state
          setPendingModification(mainOrderId, false);
        }
      }
    },
    [symbol, updateOTOInOrder, setPendingModification],
  );

  // Remove OTO order
  const removeOTO = useCallback(
    (mainOrderId, otoOrderId) => {
      const translatedSymbol = marketdata.resolveSymbol(symbol);
      removeOTOFromOrder(translatedSymbol, mainOrderId, otoOrderId);
    },
    [symbol, removeOTOFromOrder],
  );

  // Remove ALL stop orders for a main order
  const removeAllStops = useCallback(
    (mainOrderId) => {
      const mainOrder = orders.find((o) => o.id === mainOrderId);
      if (!mainOrder || !mainOrder.oto) {
        console.warn(
          "useOrderOverlay.removeAllStops: Main order not found or has no OTO orders:",
          mainOrderId,
        );
        return;
      }

      const translatedSymbol = marketdata.resolveSymbol(symbol);

      // Find all stop orders and remove them
      const stopOrders = mainOrder.oto.filter((oto) => oto.type === "stop");
      console.log(
        "🗑️ useOrderOverlay.removeAllStops: Removing all stop orders:",
        { mainOrderId, stopOrders: stopOrders.map((s) => s.id) },
      );

      stopOrders.forEach((stopOrder) => {
        removeOTOFromOrder(translatedSymbol, mainOrderId, stopOrder.id);
      });
    },
    [symbol, orders, removeOTOFromOrder],
  );

  // Remove ALL OTO orders (both stops and profits) for a main order
  const removeAllOTOs = useCallback(
    (mainOrderId) => {
      const mainOrder = orders.find((o) => o.id === mainOrderId);
      if (!mainOrder || !mainOrder.oto) {
        console.warn(
          "useOrderOverlay.removeAllOTOs: Main order not found or has no OTO orders:",
          mainOrderId,
        );
        return;
      }

      const translatedSymbol = marketdata.resolveSymbol(symbol);

      // Find all OTO orders (stops and profits) and remove them
      const allOTOOrders = mainOrder.oto;
      console.log(
        "🗑️ useOrderOverlay.removeAllOTOs: Removing ALL OTO orders:",
        {
          mainOrderId,
          otoOrders: allOTOOrders.map((o) => `${o.id}(${o.type})`),
        },
      );

      allOTOOrders.forEach((otoOrder) => {
        removeOTOFromOrder(translatedSymbol, mainOrderId, otoOrder.id);
      });
    },
    [symbol, orders, removeOTOFromOrder],
  );

  // 🔧 NEW: Unified update function for ALL order changes in a single router call
  const updateOrderComplete = useCallback(
    async (orderId, updates) => {
      if (!symbol) return;

      const { quantity: newQuantity, price: newPrice, otoUpdates } = updates;

      // Find the order to check its state
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        console.warn(
          "useOrderOverlay.updateOrderComplete: Order not found:",
          orderId,
        );
        return;
      }

      console.log("🔄 useOrderOverlay.updateOrderComplete:", {
        orderId,
        newQuantity,
        otoUpdates,
        orderState: order.state,
      });

      if (order.state === "local") {
        // Local orders: update in store
        const orderSymbol = order.symbol;
        const updateData = {};

        if (newQuantity !== undefined) {
          updateData.quantity = newQuantity;
        }

        if (newPrice !== undefined) {
          updateData.price = newPrice;
        }

        if (Object.keys(updateData).length > 0) {
          updateOrder(orderSymbol, orderId, updateData);
        }

        // Sync OTO leg quantities when main order quantity changes
        if (newQuantity !== undefined && order.oto && order.oto.length > 0) {
          order.oto.forEach((leg) => {
            updateOTOInOrder(orderSymbol, orderId, leg.id, {
              quantity: newQuantity,
            });
          });
        }

        // Update OTO prices if provided
        if (otoUpdates && otoUpdates.length > 0) {
          otoUpdates.forEach((otoUpdate) => {
            updateOTOInOrder(orderSymbol, orderId, otoUpdate.otoId, {
              price: otoUpdate.newPrice,
            });
          });
        }
      } else if (order.state === "open") {
        // Open orders: modify via router using translated symbol
        try {
          const translatedSymbol = marketdata.resolveSymbol(symbol);
          const orderSymbol = order.symbol || symbol;

          // Build replace order via unified utility
          const replaceOrderObject = buildReplaceOrder(
            order,
            {
              price: newPrice,
              quantity: newQuantity,
              otoUpdates,
            },
            translatedSymbol,
          );

          if (import.meta.env.DEV) {
            console.log(
              "🔄 useOrderOverlay.updateOrderComplete: replace order:",
              JSON.stringify(replaceOrderObject),
            );
          }

          // Optimistic local store updates (before await so UI reflects immediately)
          const localUpdateData = {};
          if (newQuantity !== undefined) localUpdateData.quantity = newQuantity;
          if (newPrice !== undefined) localUpdateData.price = newPrice;
          if (Object.keys(localUpdateData).length > 0) {
            updateOrder(orderSymbol, orderId, localUpdateData);
          }

          // Sync OTO leg quantities in local store when main order quantity changes
          if (newQuantity !== undefined && order.oto && order.oto.length > 0) {
            order.oto.forEach((leg) => {
              updateOTOInOrder(orderSymbol, orderId, leg.id, {
                quantity: newQuantity,
              });
            });
          }

          if (otoUpdates && otoUpdates.length > 0) {
            otoUpdates.forEach((otoUpdate) => {
              const normalizedOtoPrice = normalizePrice(
                translatedSymbol,
                otoUpdate.newPrice,
              );
              updateOTOInOrder(orderSymbol, orderId, otoUpdate.otoId, {
                price: normalizedOtoPrice,
              });
            });
          }

          await router.replaceOrder(replaceOrderObject);
        } catch (error) {
          console.error("❌ Failed to perform unified order update:", error);

          // Force sync with server to ensure correct state after failed replace
          try {
            await router.getAccountDataAsync();
            console.log(
              "📊 Refreshed accountData after failed unified update operation",
            );
          } catch (refreshError) {
            console.warn(
              "⚠️ Failed to refresh accountData after replace error:",
              refreshError,
            );
          }

          alert(`Erro ao modificar ordem: ${error.message || error}`);
        }
      } else {
        console.warn(
          "useOrderOverlay.updateOrderComplete: Cannot modify order in state:",
          order.state,
        );
      }
    },
    [symbol, orders, updateOrder, updateOTOInOrder],
  );

  // Initialize order store on first use
  useEffect(() => {
    const initialize = useOrderStore.getState().initialize;
    const cleanup = initialize();
    return cleanup;
  }, []);

  return {
    orders,
    createOrder,
    updateOrderPrice,
    updateOrderQuantity,
    updateOrderComplete, // 🔧 NEW: Unified update function
    submitOrder,
    removeOrder,
    // OTO methods
    createOTO,
    updateOTOPrice,
    removeOTO,
    removeAllStops,
    removeAllOTOs,
  };
};
